import { format } from 'date-fns';
import { useState } from 'react';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import { reportsApi } from '@/services/api';
import { downloadCsv } from '@/utils/csv';
import GroupByToggle from '@/components/reports/GroupByToggle';
import PurchaseReportFilters from '../shared/PurchaseReportFilters';
import ReportDataTable, { columnsToCsvHeaders, rowsToCsvData } from '../shared/ReportDataTable';
import ReportLayout from '../shared/ReportLayout';
import { fetchPurchaseReportExport, usePurchaseReport } from '../shared/usePurchaseReport';
import { ReportColumn } from '../shared/types';
import { formatMoney } from '@/utils/currency';

const invoiceColumns: ReportColumn<Record<string, unknown>>[] = [
  {
    id: 'creation',
    header: 'Date',
    cell: (r) => (r.creation ? format(new Date(r.creation as string), 'd MMM yyyy, hh:mm a') : ''),
    exportValue: (r) => String(r.creation ?? ''),
  },
  { id: 'name', header: 'Invoice ID', accessorKey: 'name' },
  { id: 'supplier_name', header: 'Supplier', accessorKey: 'supplier_name' },
  { id: 'status', header: 'Status', accessorKey: 'status' },
  {
    id: 'net_total',
    header: 'Net Total',
    align: 'right',
    cell: (r) => `${formatMoney(r.net_total as number)}`,
    exportValue: (r) => Number(r.net_total ?? 0),
  },
  {
    id: 'paid_amount',
    header: 'Paid',
    align: 'right',
    cell: (r) => `${formatMoney(r.paid_amount as number)}`,
    exportValue: (r) => Number(r.paid_amount ?? 0),
  },
  {
    id: 'balance_amount',
    header: 'Balance',
    align: 'right',
    cell: (r) => `${formatMoney(r.balance_amount as number)}`,
    exportValue: (r) => Number(r.balance_amount ?? 0),
  },
];

const itemColumns: ReportColumn<Record<string, unknown>>[] = [
  {
    id: 'creation',
    header: 'Date',
    cell: (r) => (r.creation ? format(new Date(r.creation as string), 'd MMM yyyy, hh:mm a') : ''),
    exportValue: (r) => String(r.creation ?? ''),
  },
  // { id: 'invoice', header: 'Invoice ID', accessorKey: 'invoice' },
  { id: 'supplier_name', header: 'Supplier', accessorKey: 'supplier_name' },
  { id: 'item_name', header: 'Item', accessorKey: 'item_name' },
  { id: 'quantity', header: 'Qty', accessorKey: 'quantity', align: 'right' },
  {
    id: 'rate',
    header: 'Rate',
    align: 'right',
    cell: (r) => `${formatMoney(r.rate as number)}`,
    exportValue: (r) => Number(r.rate ?? 0),
  },
  {
    id: 'amount',
    header: 'Amount',
    align: 'right',
    cell: (r) => `${formatMoney(r.amount as number)}`,
    exportValue: (r) => Number(r.amount ?? 0),
  },
];

export default function PurchaseRegisterPage() {
  const dateRangeHook = useReportDateRange('This Month');
  const { from_date, to_date } = dateRangeHook;
  const [supplier, setSupplier] = useState('');
  const [supplierLabel, setSupplierLabel] = useState('');
  const [groupBy, setGroupBy] = useState<'Invoice' | 'Item'>('Invoice');

  const params = {
    from_date,
    to_date,
    supplier: supplier || undefined,
    group_by: groupBy,
  };

  const { rows, total, page, setPage, pageSize, isLoading } = usePurchaseReport({
    queryKey: 'purchaseRegister',
    params,
    fetcher: (p) => reportsApi.getPurchaseRegisterPaginated(p),
  });

  const columns = groupBy === 'Invoice' ? invoiceColumns : itemColumns;

  const handleExport = async () => {
    const data = await fetchPurchaseReportExport(reportsApi.getPurchaseRegisterPaginated, params);
    downloadCsv(
      `purchase-register-${from_date}-${to_date}.csv`,
      columnsToCsvHeaders(columns),
      rowsToCsvData(data.rows as Record<string, unknown>[], columns),
    );
  };

  return (
    <ReportLayout
      onExport={handleExport}
      exportDisabled={!total}
      isLoading={isLoading}
      isEmpty={!rows.length}
      page={page}
      total={total}
      pageSize={pageSize}
      onPageChange={setPage}
      filters={
        <div className="space-y-4">
          <PurchaseReportFilters
            dateRangeHook={dateRangeHook}
            supplier={supplier}
            supplierLabel={supplierLabel}
            onSupplierChange={(id, label) => {
              setSupplier(id);
              setSupplierLabel(label);
            }}
          />
          <GroupByToggle groupBy={groupBy} onChange={setGroupBy} />
        </div>
      }
    >
      <ReportDataTable data={rows as Record<string, unknown>[]} columns={columns} />
    </ReportLayout>
  );
}
